import { useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { unitKeys } from "../keys/units";
import type {
  UnitResponse,
  UnitCreateRequest,
  UnitUpdateRequest,
} from "@/app/api/types";
import { UUID } from "crypto";
import { useUnits as useV2Units, useAISyncStatus } from "@stockline/web";
import type { Unit as V2Unit } from "@stockline/shared/types/inventory/unit";

function v2UnitToLegacyResponse(u: V2Unit): UnitResponse {
  return {
    id: u.id as UnitResponse["id"],
    company_id: u.companyId as UnitResponse["company_id"],
    name: u.name,
    abbreviation: u.abbreviation,
    per_box: u.perBox,
    price_per_unit: u.pricePerUnit,
    default: u.default,
    created_at: u.createdAt,
    updated_at: u.updatedAt,
  };
}

/**
 * Reads units from the V2 offline-first cache (Dexie + SSE). No longer hits
 * FastAPI `/units/`. Mutations below still hit FastAPI — the resulting SSE
 * `unit.*` events update Dexie automatically.
 */
export function useUnits() {
  const queryClient = useQueryClient();
  const v2Units = useV2Units();
  const { status: syncStatus } = useAISyncStatus();

  const data = useMemo<UnitResponse[]>(
    () => v2Units.map(v2UnitToLegacyResponse),
    [v2Units],
  );

  // Mirror into TanStack so consumers reading `unitKeys.list()` directly
  // (e.g. `usePreOrders/cacheHelpers.ts`) still see fresh data.
  useEffect(() => {
    queryClient.setQueryData(unitKeys.list(), data);
  }, [data, queryClient]);

  const isLoading = syncStatus === "syncing" && v2Units.length === 0;
  const status = isLoading ? ("pending" as const) : ("success" as const);
  const fetchStatus = isLoading ? ("fetching" as const) : ("idle" as const);

  return {
    data,
    isLoading,
    isError: false as const,
    error: null,
    isSuccess: !isLoading,
    isPending: isLoading,
    status,
    fetchStatus,
    dataUpdatedAt: Date.now(),
    refetch: async () => ({ data }) as never,
  };
}

/**
 * Hook to create a new unit
 */
export function useCreateUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UnitCreateRequest) => {
      return await api.units.postUnits(data);
    },
    onSuccess: (newUnit) => {
      // Optimistically update the cache with the new unit
      queryClient.setQueryData<UnitResponse[]>(unitKeys.list(), (old) => {
        if (!old) return [newUnit];
        return [...old, newUnit];
      });

      // Also invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: unitKeys.all });
    },
  });
}

/**
 * Hook to update an existing unit
 */
export function useUpdateUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: UUID; data: UnitUpdateRequest }) => {
      return await api.units.putUnitId(id, data);
    },
    onSuccess: (updatedUnit) => {
      // Optimistically update the cache
      queryClient.setQueryData<UnitResponse[]>(unitKeys.list(), (old) => {
        if (!old) return [updatedUnit];
        return old.map((unit) =>
          unit.id === updatedUnit.id ? updatedUnit : unit,
        );
      });

      // Also invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: unitKeys.all });
    },
  });
}

/**
 * Hook to delete a unit
 */
export function useDeleteUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: UUID) => {
      return await api.units.deleteUnitId(id);
    },
    onSuccess: (_, deletedId) => {
      // Optimistically remove from cache
      queryClient.setQueryData<UnitResponse[]>(unitKeys.list(), (old) => {
        if (!old) return [];
        return old.filter((unit) => unit.id !== deletedId);
      });

      // Also invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: unitKeys.all });
    },
  });
}
